import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { FileText, Search, AlertTriangle, Check, ChevronDown } from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import { useAuth } from '@/shared/hooks/useAuth';
import { useWorkflowStatus } from '@/shared/hooks/useWorkflows';
import { seoApi, SeoContentItem } from '../api/seoApi';
import { seoKeys } from '../queryKeys';
import { lifecycleApi } from '@/modules/strategy/api/lifecycleApi';
import { strategyKeys } from '@/modules/strategy/queryKeys';
import { StrategyBadge } from '@/modules/strategy/components';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';
import { PageHeader, Stat, type StatTone } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import styles from './SEODashboardPage.module.css';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  drafting: { label: 'Writing', color: 'var(--info)' },
  review: { label: 'Ready for Review', color: 'var(--warning)' },
  published: { label: 'Published', color: 'var(--success)' },
  done: { label: 'Done', color: 'var(--success)' },
  blocked: { label: 'Blocked', color: 'var(--error)' },
  in_progress: { label: 'In Progress', color: 'var(--info)' },
  queued: { label: 'Queued', color: 'var(--gray-600)' },
  changes_requested: { label: 'Changes Sent', color: 'var(--warning)' },
};

const TYPE_LABELS: Record<string, string> = {
  // Content plan types
  blog_post: 'Blog Post',
  landing_page: 'Landing Page',
  content_asset: 'Content Asset',
  video_script: 'Video Script',
  faq_page: 'FAQ Page',
  social_post: 'Social Post',
  email: 'Email',
  gbp_post: 'GBP Post',
  // Audit-derived types
  meta_tags: 'Meta Tags',
  schema_markup: 'Schema Markup',
  page_structure: 'Page Structure',
  performance_opt: 'Performance',
  ai_crawling: 'AI Crawling',
  trust_signals: 'Trust Signals',
  faq_content: 'FAQ Content',
  answer_block: 'Answer Block',
  service_page_content: 'Service Page Content',
  citation: 'Citation',
  gbp_optimization: 'GBP Optimization',
  nap_consistency: 'NAP Consistency',
  review_workflow: 'Review Workflow',
};

const formatType = (t: string) =>
  TYPE_LABELS[t] ?? t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const CMS_TYPES = new Set([
  'blog_post',
  'video_script',
  'faq_page',
  'landing_page',
  'content_asset',
  'meta_tags',
  'schema_markup',
  'page_structure',
  'performance_opt',
  'ai_crawling',
  'trust_signals',
  'faq_content',
  'answer_block',
  'service_page_content',
]);
const META_TYPES = new Set(['social_post']);
const GBP_TYPES = new Set(['gbp_post', 'gbp_optimization', 'review_workflow']);
const EMAIL_TYPES = new Set(['email']);

const needsFromType = (type: string): string[] => {
  if (CMS_TYPES.has(type)) return ['WordPress', 'Webflow'];
  if (META_TYPES.has(type)) return ['Meta'];
  if (GBP_TYPES.has(type)) return ['Google Business Profile'];
  if (EMAIL_TYPES.has(type)) return ['Email Platform'];
  return [];
};

const getBlockReason = (item: SeoContentItem): string | null => {
  if (item.status !== 'blocked') return null;
  if (item.blocker) return item.blocker;
  let needs = item.needsAccess ?? [];
  if (needs.length === 0) needs = needsFromType(item.type);
  if (needs.length === 0) return 'Setup needed';
  const hasCms = needs.some((n) => n === 'WordPress' || n === 'Webflow');
  const rest = needs.filter((n) => n !== 'WordPress' && n !== 'Webflow');
  const parts: string[] = [];
  if (hasCms) parts.push('CMS Connection');
  parts.push(...rest);
  return `Needs ${parts.join(' · ')}`;
};

const BUCKETS = [
  { key: 'site_content', label: 'Your Website — Content', desc: 'Pages and blog posts' },
  { key: 'external', label: 'External Platforms', desc: 'GBP, reviews, social, directories' },
  {
    key: 'site_technical',
    label: 'Technical Fixes',
    desc: 'Meta tags, schema, page speed, and other technical improvements from your SEO audit',
  },
];

export function ContentPage() {
  const { currentLocation } = useLocation();
  const { user } = useAuth();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');

  const itemsQuery = useQuery({
    queryKey: seoKeys.contentItems(locationId),
    queryFn: () => seoApi.getContentItems(locationId).then((r) => r.data),
    enabled: !!locationId,
  });

  const prefQuery = useQuery({
    queryKey: seoKeys.contentPreference(locationId),
    queryFn: () => seoApi.getContentPreference(locationId).then((r) => r.data),
    enabled: !!locationId,
  });

  const lifecycleQuery = useQuery({
    queryKey: strategyKeys.lifecycle(locationId),
    queryFn: () => lifecycleApi.getLifecycle(locationId),
    enabled: !!locationId,
    staleTime: 60_000,
  });

  const updatePrefMutation = useMutation({
    mutationFn: (pref: 'auto' | 'review') => seoApi.updateContentPreference(locationId, pref),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: seoKeys.contentPreference(locationId) }),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => seoApi.updateContentItem(id, 'published'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: seoKeys.contentItems(locationId) });
      toast.success('Content approved for publishing');
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: ({ id, feedback }: { id: string; feedback: string }) =>
      seoApi.submitContentFeedback(id, feedback),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: seoKeys.contentItems(locationId) });
      setFeedbackId(null);
      setFeedbackText('');
      toast.success('Feedback sent');
    },
  });

  const generateMutation = useMutation({
    mutationFn: () => seoApi.generateContentPlan(locationId),
    onSuccess: () => {
      toast.success('Content plan generation queued');
      queryClient.invalidateQueries({ queryKey: seoKeys.contentItems(locationId) });
    },
  });

  const {
    isRunning: contentPlanRunning,
    isFailed: contentPlanFailed,
    error: contentPlanError,
  } = useWorkflowStatus(locationId, 'content_plan', [[...seoKeys.contentItems(locationId)]]);

  const items = itemsQuery.data ?? [];
  const pref = prefQuery.data?.preference ?? 'auto';
  const strategyActive = lifecycleQuery.data?.data?.hasActiveStrategy ?? false;
  // A wizard draft was started but never activated — show "Complete Strategy".
  const strategyInProgress = lifecycleQuery.data?.data?.strategy?.status === 'in_progress';

  // Stats
  const publishedCount = items.filter(
    (i) => i.status === 'published' || i.status === 'done',
  ).length;
  const reviewCount = items.filter((i) => i.status === 'review').length;
  const inProgressCount = items.filter(
    (i) => i.status === 'in_progress' || i.status === 'drafting',
  ).length;
  const blockedCount = items.filter((i) => i.status === 'blocked').length;

  const latestContentTimestamp = items.length
    ? items.reduce((latest, item) => {
        const t = item.updatedAt ?? item.createdAt;
        return t && t > latest ? t : latest;
      }, items[0].updatedAt ?? items[0].createdAt)
    : null;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Content"
        subtitle="Content plan and publishing workflow"
        timestamp={latestContentTimestamp}
        actions={
          <button
            type="button"
            onClick={() => generateMutation.mutate()}
            disabled={!strategyActive || generateMutation.isPending}
            className={clsx(
              styles.generateBtn,
              strategyActive ? styles.generateBtnActive : styles.generateBtnDisabled,
            )}
            title={
              !strategyActive ? 'Create a strategy first to generate content recommendations' : ''
            }
          >
            {generateMutation.isPending ? 'Generating...' : 'Generate Content Plan'}
          </button>
        }
      />

      {/* Workflow status banners */}
      {contentPlanRunning && (
        <div className={styles.workflowCard}>
          <div className={styles.workflowSpinner} />
          <div className={styles.workflowTitle}>Generating Content Plan</div>
          <div className={styles.workflowSubtext}>
            Creating content calendar, landing pages, and content assets from your approved
            strategies. Check back in 5–10 minutes.
          </div>
        </div>
      )}
      {contentPlanFailed && (
        <div className={styles.workflowCardFailed}>
          <div className={styles.workflowTitle}>Content Plan Generation Failed</div>
          <div className={styles.workflowSubtext}>
            Something went wrong generating the content plan. Please try again.
          </div>
          {user?.isAgency && contentPlanError && (
            <div className={styles.workflowErrorDetails}>{contentPlanError}</div>
          )}
        </div>
      )}

      {!contentPlanRunning && !contentPlanFailed && itemsQuery.isLoading && <SkeletonCard />}

      {!contentPlanRunning && !contentPlanFailed && !itemsQuery.isLoading && items.length === 0 && (
        <EmptyState
          icon={<FileText size={40} strokeWidth={1.5} aria-hidden />}
          title="No content items yet"
          description={
            strategyActive
              ? 'Generate a content plan to create content recommendations based on your strategy.'
              : 'Create a marketing strategy first, then generate a content plan.'
          }
          action={
            strategyActive
              ? {
                  label: generateMutation.isPending ? 'Generating...' : 'Generate Content Plan',
                  onClick: () => generateMutation.mutate(),
                }
              : {
                  label: strategyInProgress ? 'Complete Strategy →' : 'Create Strategy →',
                  onClick: () => navigate('/strategy'),
                }
          }
        />
      )}

      {!contentPlanRunning && !contentPlanFailed && !itemsQuery.isLoading && items.length > 0 && (
        <>
          {/* Content Publishing Preference */}
          <div className={styles.preferenceCard}>
            <div className={styles.preferenceTitle}>Content Publishing</div>
            <div className={styles.preferenceGrid}>
              {[
                {
                  id: 'auto' as const,
                  label: 'Just handle it',
                  desc: 'PracticeNova writes and publishes automatically',
                },
                {
                  id: 'review' as const,
                  label: 'Review before publishing',
                  desc: 'You review drafts before we publish',
                },
              ].map((opt) => {
                const active = pref === opt.id;
                return (
                  <div
                    key={opt.id}
                    onClick={() => updatePrefMutation.mutate(opt.id)}
                    className={clsx(
                      styles.preferenceOption,
                      active && styles.preferenceOptionActive,
                    )}
                  >
                    <div
                      className={clsx(
                        styles.preferenceOptionLabel,
                        active && styles.preferenceOptionLabelActive,
                      )}
                    >
                      {opt.label}
                    </div>
                    <div className={styles.preferenceOptionDesc}>{opt.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stats Row */}
          <div className={styles.statGrid4}>
            {(
              [
                { label: 'Published / Done', value: publishedCount, tone: 'success' },
                { label: 'Ready for Review', value: reviewCount, tone: 'warning' },
                { label: 'In Progress', value: inProgressCount, tone: 'info' },
                { label: 'Blocked', value: blockedCount, tone: 'error' },
              ] as { label: string; value: number; tone: StatTone }[]
            ).map((stat) => (
              <Stat
                key={stat.label}
                card
                label={stat.label}
                value={stat.value}
                tone={stat.value > 0 ? stat.tone : 'muted'}
              />
            ))}
          </div>

          {/* Content items grouped by bucket */}
          {BUCKETS.map((bucket) => {
            const bucketItems = items
              .filter((i) => i.bucket === bucket.key)
              .slice()
              .sort((a, b) => {
                const aw = a.week ?? Number.POSITIVE_INFINITY;
                const bw = b.week ?? Number.POSITIVE_INFINITY;
                if (aw !== bw) return aw - bw;
                return a.title.localeCompare(b.title);
              });
            if (bucketItems.length === 0) return null;

            return (
              <div key={bucket.key}>
                <div className={styles.bucketTitle}>{bucket.label}</div>
                <div className={styles.bucketDesc}>{bucket.desc}</div>
                <div className={styles.itemList}>
                  {bucketItems.map((item) => {
                    const isExp = expanded === item.id;
                    const sc = STATUS_CONFIG[item.status] ?? STATUS_CONFIG.queued;
                    const showApproval =
                      pref === 'review' &&
                      item.status === 'review' &&
                      item.bucket === 'site_content';
                    const isDone = item.status === 'done' || item.status === 'published';
                    const blockReason = getBlockReason(item);

                    return (
                      <div
                        key={item.id}
                        className={clsx(styles.itemRow, isDone && styles.itemRowDone)}
                      >
                        <div
                          onClick={() => setExpanded(isExp ? null : item.id)}
                          className={styles.itemHead}
                        >
                          <div className={styles.itemHeadMain}>
                            <div className={styles.itemHeadTitleRow}>
                              <span className={styles.itemTitle}>{item.title}</span>
                              <span
                                className={styles.itemStatusPill}
                                style={{ background: `${sc.color}15`, color: sc.color }}
                              >
                                {item.status === 'blocked' && blockReason ? blockReason : sc.label}
                              </span>
                              {item.strategyItemTitle && (
                                <StrategyBadge title={item.strategyItemTitle} />
                              )}
                              {item.auditResultId && (
                                <span
                                  className={clsx(
                                    styles.pill,
                                    styles.pillInfo,
                                    styles.pillUppercase,
                                  )}
                                >
                                  <Search size={11} aria-hidden /> Website Audit
                                </span>
                              )}
                            </div>
                            <div className={styles.itemMeta}>
                              {formatType(item.type)}
                              {item.week ? ` · Week ${item.week}` : ''}
                              {item.language && item.language !== 'en' ? ` · ${item.language}` : ''}
                              {item.wordCount ? ` · ${item.wordCount}` : ''}
                              {item.blocker && item.status !== 'blocked' && (
                                <>
                                  {' · '}
                                  <AlertTriangle size={11} aria-hidden /> {item.blocker}
                                </>
                              )}
                            </div>
                          </div>
                          <div className={styles.itemActions}>
                            {showApproval && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  approveMutation.mutate(item.id);
                                }}
                                className={styles.itemApproveBtn}
                              >
                                Approve & Publish
                              </button>
                            )}
                            {isDone && (
                              <span className={styles.itemDone}>
                                <Check size={14} aria-hidden />
                              </span>
                            )}
                            <span className={clsx(styles.chevron, isExp && styles.chevronExpanded)}>
                              <ChevronDown size={14} aria-hidden />
                            </span>
                          </div>
                        </div>

                        {isExp && (
                          <div className={styles.itemDrawer}>
                            {/* Draft preview for content items in review */}
                            {item.draftSummary && showApproval && (
                              <div className={styles.draftPreview}>
                                <div className={styles.draftPreviewLabel}>Draft Preview</div>
                                <div className={styles.draftPreviewBody}>{item.draftSummary}</div>
                                <div className={styles.draftPreviewActions}>
                                  <button
                                    type="button"
                                    onClick={() => approveMutation.mutate(item.id)}
                                    className={styles.btnApprove}
                                  >
                                    Approve & Publish
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setFeedbackId(feedbackId === item.id ? null : item.id)
                                    }
                                    className={styles.btnRequestChanges}
                                  >
                                    Request Changes
                                  </button>
                                </div>
                                {feedbackId === item.id && (
                                  <div className={styles.feedbackBlock}>
                                    <textarea
                                      placeholder="Tell us what to change..."
                                      value={feedbackText}
                                      onChange={(e) => setFeedbackText(e.target.value)}
                                      className={styles.feedbackTextarea}
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        feedbackMutation.mutate({
                                          id: item.id,
                                          feedback: feedbackText,
                                        })
                                      }
                                      disabled={!feedbackText.trim()}
                                      className={styles.btnSendFeedback}
                                    >
                                      Send Feedback
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Writer brief from plan generation */}
                            {item.brief && !showApproval && (
                              <div className={styles.briefBlock}>
                                <div className={styles.briefLabel}>Brief</div>
                                <div className={styles.briefBody}>{item.brief}</div>
                              </div>
                            )}

                            {/* Needs access */}
                            {item.needsAccess && item.needsAccess.length > 0 && (
                              <div className={styles.needsAccessRow}>
                                {item.needsAccess.map((a, j) => (
                                  <button
                                    type="button"
                                    key={j}
                                    onClick={() => navigate('/settings/organic')}
                                    className={styles.connectionChip}
                                  >
                                    Connect {a}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
